import { useState, useEffect, useRef } from 'react'
import { Box, Typography, TextField, Button, Alert } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '~/supabase-client'
import { validateTicket } from './ticket-utils'
import { useAuthContext } from '~/common/context/auth/auth-context'

export default function Auth() {
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [ticketError, setTicketError] = useState(null)
  const [ticketExpiry, setTicketExpiry] = useState(null)
  const { enqueueSnackbar } = useSnackbar()
  const [searchParams] = useSearchParams()
  const autoLoginAttempted = useRef(false)
  const { setTicketSession } = useAuthContext()

  const inviteTicket = searchParams.get('inviteTicket')
  const isInviteMode = !!inviteTicket

  // Check for demo ticket in URL and auto-login
  useEffect(() => {
    const ticket = searchParams.get('ticket')
    if (!ticket || autoLoginAttempted.current) return

    autoLoginAttempted.current = true

    async function processTicket() {
      const result = await validateTicket(ticket)

      if (!result.valid) {
        setTicketError(result.error)
        if (result.expiresAt) {
          setTicketExpiry(result.expiresAt)
        }
        return
      }

      // Valid ticket - auto-login
      setLoading(true)
      const { error } = await supabase.auth.signInWithPassword({
        email: result.credentials.email,
        password: result.credentials.password
      })

      if (error) {
        enqueueSnackbar(error.error_description || error.message, { variant: 'error' })
        setLoading(false)
      } else {
        // Store ticket info for sidebar display
        setTicketSession(ticket, result.expiresAt)
      }
      // On success, AuthContext will detect the session change and redirect
    }

    processTicket()
  }, [searchParams, enqueueSnackbar, setTicketSession])

  const handlePasswordLogin = async (event) => {
    event.preventDefault()

    setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (error) {
      enqueueSnackbar(error.error_description || error.message, { variant: 'error' })
    }
    setLoading(false)
  }

  const handleInviteSignup = async (event) => {
    event.preventDefault()

    if (password !== confirmPassword) {
      enqueueSnackbar('Passwords do not match', { variant: 'error' })
      return
    }

    setLoading(true)
    const { error } = await supabase.auth.signUp({ email, password })

    if (error) {
      enqueueSnackbar(error.error_description || error.message, { variant: 'error' })
      setLoading(false)
      return
    }

    // Persist the invite ticket so set-password can pick it up.
    // Don't navigate manually — Login.jsx watches auth state and will navigate
    // to /set-password once needsProfileSetup is true.
    sessionStorage.setItem('pendingInviteTicket', inviteTicket)
    setLoading(false)
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          width: { xs: '96%', sm: '46%' },
        }}
      >
        <Typography variant="h4" component="h1">
          TerraPlanner
        </Typography>

        {isInviteMode ? (
          <>
            <Alert severity="info">
              You&apos;ve been invited to join a farm on TerraPlanner. Create your account to get started.
            </Alert>

            <Box
              component="form"
              onSubmit={handleInviteSignup}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
            >
              <TextField
                type="email"
                label="Email"
                placeholder="Your email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
              <TextField
                type="password"
                label="Password"
                placeholder="Choose a password"
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
              />
              <TextField
                type="password"
                label="Confirm Password"
                placeholder="Repeat your password"
                value={confirmPassword}
                required
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Access is by invite only. If you received an invite email, use the link in that email to set up your account.
            </Typography>

            {ticketError && (
              <Alert severity="warning">
                {ticketError === 'Ticket has expired' && ticketExpiry ? (
                  <>
                    <strong>Access link expired</strong>
                    <br />
                    This demo access link expired on {ticketExpiry.toLocaleDateString()}.
                    Please contact me for a new link.
                  </>
                ) : (
                  <>
                    <strong>Invalid access link</strong>
                    <br />
                    The access link is not valid. Please contact me for a valid link.
                  </>
                )}
              </Alert>
            )}

            {loading && searchParams.get('ticket') && (
              <Alert severity="info">
                Signing in with demo access...
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handlePasswordLogin}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
            >
              <TextField
                type="email"
                label="Email"
                placeholder="Your email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
              <TextField
                type="password"
                label="Password"
                placeholder="Your password"
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                {loading ? 'Loading...' : 'Sign In'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
